import { Component, EventEmitter, Input, Output } from '@angular/core';

// Types
export type ActionButtonVariant =
  | 'primary' // Botón principal (azul)
  | 'success' // Confirmar/Guardar (verde)
  | 'danger' // Eliminar/Cancelar destructivo (rojo)
  | 'warning' // Advertencia (amarillo/naranja)
  | 'secondary' // Cancelar/Secundario (gris)
  | 'info' // Información (azul claro)
  | 'cancel' // Cancelar específico (gris claro con efectos especiales)
  | 'custom'; // Personalizado

export type ActionButtonSize = 'small' | 'normal' | 'large';

export interface ActionButtonConfig {
  variant: ActionButtonVariant;
  label: string;
  icon?: string;
  size?: ActionButtonSize;
  disabled?: boolean;
  loading?: boolean;
  outlined?: boolean;
  rounded?: boolean;
  block?: boolean;
  customClass?: string;
}

@Component({
  selector: 'app-action-button',
  templateUrl: './action-button.component.html',
  styleUrls: ['./action-button.component.scss'],
})
export class ActionButtonComponent {
  @Input() variant: ActionButtonVariant = 'primary';
  @Input() label!: string;
  @Input() icon?: string;
  @Input() size: ActionButtonSize = 'normal';
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() outlined: boolean = false;
  @Input() rounded: boolean = false;
  @Input() block: boolean = false;
  @Input() customClass?: string;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() tooltip?: string;

  @Output() buttonClick = new EventEmitter<MouseEvent>();

  // Métodos de conveniencia para crear botones predefinidos
  static cancel(
    label: string = 'Cancelar',
    options?: Partial<ActionButtonConfig>,
  ): ActionButtonConfig {
    return {
      variant: 'cancel',
      label,
      icon: 'pi pi-pencil',
      outlined: true,
      ...options,
    };
  }

  static confirm(
    label: string = 'Confirmar',
    options?: Partial<ActionButtonConfig>,
  ): ActionButtonConfig {
    return {
      variant: 'success',
      label,
      icon: 'pi pi-check',
      ...options,
    };
  }

  static save(
    label: string = 'Guardar',
    options?: Partial<ActionButtonConfig>,
  ): ActionButtonConfig {
    return {
      variant: 'primary',
      label,
      icon: 'pi pi-save',
      ...options,
    };
  }

  static delete(
    label: string = 'Eliminar',
    options?: Partial<ActionButtonConfig>,
  ): ActionButtonConfig {
    return {
      variant: 'danger',
      label,
      icon: 'pi pi-trash',
      outlined: true,
      ...options,
    };
  }

  static edit(label: string = 'Editar', options?: Partial<ActionButtonConfig>): ActionButtonConfig {
    return {
      variant: 'info',
      label,
      icon: 'pi pi-pencil',
      ...options,
    };
  }

  static search(
    label: string = 'Buscar',
    options?: Partial<ActionButtonConfig>,
  ): ActionButtonConfig {
    return {
      variant: 'primary',
      label,
      icon: 'pi pi-search',
      ...options,
    };
  }

  static reset(
    label: string = 'Limpiar',
    options?: Partial<ActionButtonConfig>,
  ): ActionButtonConfig {
    return {
      variant: 'warning',
      label,
      icon: 'pi pi-refresh',
      outlined: true,
      ...options,
    };
  }

  get buttonClasses(): string {
    const classes = ['action-button'];

    // Variante
    classes.push(`action-button--${this.variant}`);

    // Tamaño
    if (this.size !== 'normal') {
      classes.push(`action-button--${this.size}`);
    }

    // Modificadores
    if (this.outlined) {
      classes.push('action-button--outlined');
    }

    if (this.rounded) {
      classes.push('action-button--rounded');
    }

    if (this.block) {
      classes.push('action-button--block');
    }

    if (this.loading) {
      classes.push('action-button--loading');
    }

    if (this.disabled) {
      classes.push('action-button--disabled');
    }

    // Clase personalizada
    if (this.customClass) {
      classes.push(this.customClass);
    }

    return classes.join(' ');
  }

  get primeButtonClasses(): string {
    const classes = [];

    // Tamaño PrimeNG
    switch (this.size) {
      case 'small':
        classes.push('p-button-sm');
        break;
      case 'large':
        classes.push('p-button-lg');
        break;
    }

    // Severidad PrimeNG
    switch (this.variant) {
      case 'success':
        classes.push('p-button-success');
        break;
      case 'danger':
        classes.push('p-button-danger');
        break;
      case 'warning':
        classes.push('p-button-warning');
        break;
      case 'info':
        classes.push('p-button-info');
        break;
      case 'secondary':
        classes.push('p-button-secondary');
        break;
      case 'cancel':
        classes.push('p-button-secondary'); // Usa secondary como base para PrimeNG
        break;
    }

    // Modificadores PrimeNG
    if (this.outlined) {
      classes.push('p-button-outlined');
    }

    if (this.rounded) {
      classes.push('p-button-rounded');
    }

    return classes.join(' ');
  }

  onButtonClick(event: MouseEvent): void {
    if (!this.disabled && !this.loading) {
      this.buttonClick.emit(event);
    }
  }
}
